// Catalog.ID Client — Key Bag Management
// ============================================================================
// Each key bag entry is encrypted individually with AES-256-GCM using a key
// derived from the supersecret via Argon2id. The server stores entries as
// individual rows (one per key), enabling per-entry sync in federation.

import * as aes from './aes';
import { deriveKeyFromSupersecret, generateSalt } from './argon2';

// ============================================================================
// Types
// ============================================================================

export interface KeyBagEntry {
    keyId: string;
    algorithm: string;
    privateKey: string;  // base64-encoded private key bytes
    keyIndex?: number;   // key generation index (0 = original, 1+ = rotated). Default 0
    status?: 'active' | 'revoked';  // default 'active'
}

export interface KeyBag {
    entries: KeyBagEntry[];
}

/** A single entry as stored on the server (encrypted per-entry). */
export interface EncryptedKeyBagEntry {
    keyId: string;
    keyIndex: number;
    /**
     * Open algorithm tag matching the corresponding accountpublickey row's algorithm.
     * Server enforces consistency at write time in KeyBagEntryRepository.
     * The same algorithm is also AES-GCM-authenticated inside encryptedPrivateKey;
     * decryptEntry verifies the two agree.
     */
    algorithm: string;
    encryptedPrivateKey: string;  // base64 ciphertext of JSON { algorithm, privateKey }
    nonce: string;                // base64 AES-256-GCM nonce
    status?: string;
}

/** The server response for GET /api/account/keybag. */
export interface KeyBagServerResponse {
    keySalt: string;
    entries: EncryptedKeyBagEntry[];
}

/** Result of encrypting a full key bag for registration or replacement. */
export interface EncryptedKeyBagData {
    keySalt: string;
    entries: EncryptedKeyBagEntry[];
}

// ============================================================================
// Per-entry encryption/decryption
// ============================================================================

/** Encrypt a single key bag entry's private data. */
export async function encryptEntry(
    entry: KeyBagEntry,
    derivedKey: Uint8Array,
): Promise<EncryptedKeyBagEntry> {
    const nonce = aes.generateNonce();
    const plaintext = aes.encodeUtf8(JSON.stringify({
        algorithm: entry.algorithm,
        privateKey: entry.privateKey,
    }));
    const ciphertext = await aes.encrypt(derivedKey, nonce, plaintext);
    return {
        keyId: entry.keyId,
        keyIndex: entry.keyIndex ?? 0,
        algorithm: entry.algorithm,
        encryptedPrivateKey: aes.toBase64(ciphertext),
        nonce: aes.toBase64(nonce),
        status: entry.status ?? 'active',
    };
}

/** Decrypt a single server-side entry back into a KeyBagEntry.
 *  Verifies that the open `algorithm` field on the entry matches the algorithm
 *  inside the AES-GCM-authenticated payload — defends against a server that
 *  silently mutates the open algorithm field. */
export async function decryptEntry(
    entry: EncryptedKeyBagEntry,
    derivedKey: Uint8Array,
): Promise<KeyBagEntry> {
    const nonce = aes.fromBase64(entry.nonce);
    const ciphertext = aes.fromBase64(entry.encryptedPrivateKey);
    const plaintext = await aes.decrypt(derivedKey, nonce, ciphertext);
    const { algorithm, privateKey } = JSON.parse(aes.decodeUtf8(plaintext));
    if (entry.algorithm && entry.algorithm !== algorithm) {
        throw new Error(
            `Key bag algorithm mismatch for ${entry.keyId}#${entry.keyIndex}: ` +
            `open='${entry.algorithm}' but encrypted='${algorithm}'`,
        );
    }
    return {
        keyId: entry.keyId,
        algorithm,
        privateKey,
        keyIndex: entry.keyIndex,
        status: (entry.status as 'active' | 'revoked') ?? 'active',
    };
}

// ============================================================================
// Bulk encrypt/decrypt (for registration, restore, rotation)
// ============================================================================

/** Encrypt all entries in a key bag. Generates a new Argon2id salt. */
export async function encryptKeyBag(bag: KeyBag, supersecret: string): Promise<EncryptedKeyBagData> {
    const salt = generateSalt();
    const derivedKey = await deriveKeyFromSupersecret(supersecret, salt);
    const entries = await Promise.all(
        bag.entries.map(e => encryptEntry(e, derivedKey)),
    );
    return {
        keySalt: aes.toBase64(salt),
        entries,
    };
}

/** Decrypt all entries from a server response back into a KeyBag. */
export async function decryptKeyBag(
    response: KeyBagServerResponse,
    supersecret: string,
): Promise<KeyBag> {
    return (await decryptKeyBagWithKey(response, supersecret)).bag;
}

/**
 * Decrypt the bag and also surface the derived AES key. The Phase-6 Key
 * Grant save handler keeps this around so it can encrypt new entries
 * (under the same Argon2 salt) without re-prompting the user for the
 * supersecret.
 */
export async function decryptKeyBagWithKey(
    response: KeyBagServerResponse,
    supersecret: string,
): Promise<{ bag: KeyBag; derivedKey: Uint8Array }> {
    const salt = aes.fromBase64(response.keySalt);
    const derivedKey = await deriveKeyFromSupersecret(supersecret, salt);
    const entries = await Promise.all(
        response.entries.map(e => decryptEntry(e, derivedKey)),
    );
    return { bag: { entries }, derivedKey };
}

// ============================================================================
// In-memory key bag manipulation (unchanged)
// ============================================================================

/** Add or replace an entry in the key bag. For key rotation, use addKeyBagEntry instead. */
export function setKeyBagEntry(bag: KeyBag, entry: KeyBagEntry): KeyBag {
    const entries = bag.entries.filter(e => e.keyId !== entry.keyId);
    entries.push(entry);
    return { entries };
}

/** Get the active entry for a key ID (highest keyIndex with status 'active'). */
export function getKeyBagEntry(bag: KeyBag, keyId: string): KeyBagEntry | undefined {
    return bag.entries
        .filter(e => e.keyId === keyId && (e.status ?? 'active') === 'active')
        .sort((a, b) => (b.keyIndex ?? 0) - (a.keyIndex ?? 0))[0];
}

/** Add a new entry without removing existing ones (for key rotation — old keys stay as revoked). */
export function addKeyBagEntry(bag: KeyBag, entry: KeyBagEntry): KeyBag {
    return { entries: [...bag.entries, entry] };
}

/** Get all entries for a key ID, sorted by keyIndex ascending (for chain-walking decryption). */
export function getKeyChain(bag: KeyBag, keyId: string): KeyBagEntry[] {
    return bag.entries
        .filter(e => e.keyId === keyId)
        .sort((a, b) => (a.keyIndex ?? 0) - (b.keyIndex ?? 0));
}

/**
 * Key IDs that live in the key bag with deliberately NO `accountpublickey`
 * row — the server's `KeyBagEntryRepository.VerifyAlgorithmConsistencyAsync`
 * skip-list. Sending one of these in a registration/activation `publicKeys`
 * payload would publish it on the account's public key surface, which is
 * exactly what each of them is defined to avoid:
 *
 *  - `chat_sign`          — the chat account root. Its public half publishes
 *    on the `registry_signing_key` CLAIM track under the `chat_root` key
 *    class (chat migration §3). Registering it as an account public key would
 *    hand a chat root the EFS-retrieval and OIDC machine-key authority the
 *    separate key exists to withhold.
 *  - `mail_sign`          — the mail account root, `chat_sign`'s twin. Its
 *    public half publishes on the same CLAIM track under the `mail_root` key
 *    class (mail migration §5.2), for the same single reason: verification of
 *    years-old archived mail needs the track's {publishedAt, revokedAt}
 *    validity window. Registering it as an account public key would hand a
 *    mail root the EFS-retrieval and OIDC machine-key authority the separate
 *    key exists to withhold.
 *  - `derive_root`        — the account's HKDF root for the `derive` keybag op
 *    (EFS lineage-labels §5b). A 32-byte symmetric secret with no public half
 *    at all, so there is nothing an `accountpublickey` row could hold;
 *    publishing one would also advertise which accounts can derive labeled
 *    KEKs.
 *  - `xrpl_master`        — the identity's XRPL master signing key (round 5,
 *    2026-09-03). An XRPL address is a hash of the public key, so an
 *    `accountpublickey` row would link the CID account to its XRPL address
 *    for anyone reading the public key surface — the same linkage argument
 *    `chat_sign` makes about chat, with money attached.
 *  - `recovery_kit`       — the Recovery Kit's stored set (round 9, 2026-09-10;
 *    registration-restructure §16.5). Polynomial coefficients: a symmetric
 *    secret with no public half at all, like `derive_root`. It is additionally
 *    the ONE entry that must never reach a device — see
 *    {@link isDeviceStorableKeyId} — and must never be wrapped for a delegate,
 *    which is why it appears in no org key entitlement.
 *  - `bitcash_wallet`     — an ORGANIZATION's bitcash wallet key (round 10,
 *    2026-09-14; registration-wallets §4.8, D14). A bitcash key has no public
 *    half to publish, and a row would link the account to the wallet for anyone
 *    reading the public key surface — the `xrpl_master` argument, with money
 *    attached. It IS wrapped for a delegate: an organization's wallet is an
 *    entitled org key for owner and treasurer.
 *
 *    PERSONAL wallets no longer appear in a bag at all (cid-encrypted-storage
 *    §7.2, 2026-09-17): they live in the account's `cid/bitcash-wallets` blob.
 *    The id stays in these vocabularies — and in the server's `KnownKeyTypes` —
 *    because ORGANIZATION bags still carry it, orgs having no derive root their
 *    delegates can use (§5.1).
 *
 * `mail_encrypt` is deliberately NOT here: like `chat_encrypt` it IS published
 * under the account, because the mail directory hands its public half out at
 * address-resolve time (gateway-api §6).
 *
 * `xrpl_data_key` was here too — the AES-256 key the XRPL module used to wrap
 * its local wallet vault, pushed into the bag through the Key Grant `save`
 * modes. The id is GONE from every vocabulary (cid-encrypted-storage §6, M6b,
 * 2026-09-17): the module now DERIVES its vault-wrap key from `derive_root`
 * and keeps the vault itself as the `xrpl/vault` account blob, so the bag
 * never held the thing that was actually worth recovering. Pre-live, nothing
 * is migrated — an account still carrying the entry must be recreated.
 *
 * `registry_authority` was here — the CPR account-stream authority, bag-only
 * because publishing it would link a CID account to the Registry streams it
 * owns (asset-ownership D15). The id is GONE from every vocabulary, here and
 * in the server's `KnownKeyTypes` (cid-encrypted-storage §5.2, M5, 2026-09-17):
 * the key is DERIVED from `derive_root` per generation and only its generation
 * state is stored, as the `cid/registry-authority` blob. The linkage argument
 * is unchanged and is now moot — there is no entry of any kind to publish.
 *
 * Agent-prefixed `agent:{cid}:agent_signing_key` entries are bag-only too;
 * `isBagOnlyKeyId` recognises them by suffix.
 */
export const BAG_ONLY_KEY_IDS: readonly string[] = [
    'xrpl_master',
    'chat_sign',
    'mail_sign',
    'derive_root',
    'recovery_kit',
    'bitcash_wallet',
];

/** True when `keyId` must NOT appear in an `accountpublickey` write path. */
export function isBagOnlyKeyId(keyId: string): boolean {
    if (BAG_ONLY_KEY_IDS.includes(keyId)) return true;
    return keyId.startsWith('agent:') && keyId.endsWith(':agent_signing_key');
}

/**
 * Key IDs that must NEVER be written into device storage (registration-
 * restructure §16.5). Every bag-to-device write goes through this predicate:
 * device setup's loop after a supersecret unlock or a bundle restore, the
 * finalize sequence's copy of the keys IT minted, and any future one.
 *
 * ONE member:
 *
 *   `recovery_kit`    the entry's coefficients plus ONE Recovery Sheet
 *                     reconstruct the supersecret. A device holds an account's
 *                     KEYS; the root that opens them is meant to live on paper
 *                     and in the member's head. Copying it would also buy
 *                     nothing — every print needs the supersecret for the
 *                     polynomials' constant terms anyway.
 *
 * `bitcash_wallet` was the second member until 2026-09-17, when D5 was relaxed
 * (cid-encrypted-storage §7, ruling of that date): a personal wallet key is
 * PIN-level like every other device-storable secret, and the list of them moved
 * out of the bag entirely into `cid/bitcash-wallets`. Removing it from this
 * list is mostly moot — a personal bag written since then holds no entry of
 * that id at all — but it matters for the two bags that still can: a legacy
 * dev account, and an ORGANIZATION's. A never-device-stored id can never be
 * "caught up", so the PIN-unlock reconcile (`findEntriesMissingFromDevice`)
 * had to skip it or loop; a device-storable one is simply stored at the next
 * supersecret unlock and converges. An organization's entry never reaches a
 * device in practice anyway: the device belongs to the delegate, not to the
 * organization, and `finalizeCredentials` refuses a cross-account device copy
 * outright.
 *
 * Bag-only is a different question (`isBagOnlyKeyId`, about the account's
 * PUBLIC key surface) and every other bag-only key IS device-storable.
 */
const NEVER_DEVICE_STORED_KEY_IDS: readonly string[] = ['recovery_kit'];

export function isDeviceStorableKeyId(keyId: string): boolean {
    return !NEVER_DEVICE_STORED_KEY_IDS.includes(keyId);
}

/** Closed vocabulary mirroring the server's `KnownKeyTypes.GetRequiredAlgorithm`. */
const REQUIRED_ALGORITHM_BY_KEY_ID: Record<string, string> = {
    attr_encrypt: 'X25519-MLKem1024',
    msg_encrypt: 'X25519-MLKem1024',
    efs_encrypt: 'X25519-MLKem1024',
    chat_encrypt: 'X25519-MLKem1024',
    mail_encrypt: 'X25519-MLKem1024',
    main_signing_key: 'Ed25519-MLDsa65',
    agent_signing_key: 'Ed25519-MLDsa65',
    chat_sign: 'Ed25519-MLDsa65',
    mail_sign: 'Ed25519-MLDsa65',
    // Plain Ed25519 (round 5, 2026-09-03; was secp256k1 pre-implementation):
    // the XRPL module (apps/xrpl) derives ed25519-only, xrpl.js's modern
    // default. The bag stores the 16-byte family-seed entropy (crypto/xrpl.ts).
    xrpl_master: 'Ed25519',
    derive_root: 'HKDF-SHA512',
    // Round 9: Shamir shares over GF(2⁸) with the AES reduction polynomial.
    // Named for the material, following the derive_root/HKDF-SHA512 precedent.
    // The constants themselves live in crypto/recovery-kit-entry.ts; the
    // literal is repeated here the way every other id in this table is, so
    // this module stays free of imports from the features that use it.
    recovery_kit: 'SSS-GF256',
    // Round 10: a bitcash wallet key (registration-wallets §4.8). Named for the
    // material, like the two above; the literal is repeated here the way every
    // other id in this table is, so this module stays free of imports from the
    // features that use it. ORGANIZATION bags only since cid-encrypted-storage
    // §7.2 — see BAG_ONLY_KEY_IDS.
    bitcash_wallet: 'BITCASH-CASH',
};

/**
 * Every key ID this client knows — the vocabulary above, in declaration order.
 * An agent key is spelled here by its SUFFIX (`agent_signing_key`), the way
 * {@link requiredAlgorithmForKeyId} resolves it.
 *
 * Exported for the device-key catalogue's completeness test
 * (`state/device-key-selection.test.ts`): every id here must be placed
 * somewhere — a picker row, the never-stored list, or a documented exception —
 * so that adding a key id can never silently leave a device unable to offer it.
 */
export const KNOWN_KEY_IDS: readonly string[] = Object.keys(REQUIRED_ALGORITHM_BY_KEY_ID);

/**
 * The algorithm a given key ID is REQUIRED to carry, or null if the ID is not
 * recognised. Mirrors the server's `KnownKeyTypes.GetRequiredAlgorithm`, which
 * every server write path enforces — so this is the authority, not a guess.
 *
 * Needed because the `accountpublickey` directory cannot answer for bag-only
 * keys: they have no row there by design (see `BAG_ONLY_KEY_IDS`). Resolving an
 * org key's algorithm from the directory alone throws on `chat_sign`.
 *
 * Agent-prefixed IDs (`agent:{cid}:{keyType}`) resolve through their suffix.
 */
export function requiredAlgorithmForKeyId(keyId: string): string | null {
    let lookupId = keyId;
    if (keyId.startsWith('agent:')) {
        const lastColon = keyId.lastIndexOf(':');
        if (lastColon <= 'agent:'.length - 1) return null;
        lookupId = keyId.slice(lastColon + 1);
    }
    return REQUIRED_ALGORITHM_BY_KEY_ID[lookupId] ?? null;
}

/**
 * The key IDs a personal key rotation replaces. `efs_encrypt` is included
 * because a compromise-driven lockdown must not leave the EFS key live
 * (EFS WP §12.6). Everything NOT listed here — `main_signing_key`, the
 * chat identity keys `chat_encrypt` / `chat_sign`, and the mail identity keys
 * `mail_encrypt` / `mail_sign` — is carried through untouched: chat- and
 * mail-key rotation is a separate, per-key ceremony (chat migration plan O7;
 * mail plan §5.1 inherits it), and `POST /api/account/rotate-keys` replaces the
 * stored bag with exactly what the client submits, so an entry this
 * transform drops is destroyed rather than merely stale. `mail_encrypt` in
 * particular must stay absent: every archived envelope is wrapped to a
 * generation of it, and decryption of years-old mail rides keybag retention of
 * the rotated-out private entries (plan §5.1).
 *
 * `derive_root` is deliberately absent and must STAY absent: rotating it is an
 * owner-run estate sweep (mint root_v2, re-derive every labeled KEK, re-wrap,
 * revoke the v1 wraps — EFS lineage-labels §5b), not a side effect of a
 * personal encryption-key rotation. Silently replacing it here would strand
 * every labeled package whose KEK the old root derives.
 */
export const PERSONAL_ROTATED_KEY_IDS = ['attr_encrypt', 'msg_encrypt', 'efs_encrypt'] as const;

export type PersonalRotatedKeyId = typeof PERSONAL_ROTATED_KEY_IDS[number];

/**
 * Apply a personal key rotation to a decrypted bag: revoke the current
 * generation of each rotated key (kept for chain-walking historical claims,
 * WP §3.4.8) and append the replacement at `keyIndex + 1`. Every other entry
 * is preserved verbatim, including its status and key index.
 *
 * `newPrivateKeys` values are base64 hybrid-KEM secret keys.
 */
export function rotatePersonalEncryptionKeys(
    bag: KeyBag,
    newPrivateKeys: Record<PersonalRotatedKeyId, string>,
): KeyBag {
    let next = bag;
    for (const keyId of PERSONAL_ROTATED_KEY_IDS) {
        const current = getKeyBagEntry(next, keyId);
        const currentIndex = current?.keyIndex ?? 0;
        next = revokeKeyBagEntry(next, keyId, currentIndex);
        next = addKeyBagEntry(next, {
            keyId,
            algorithm: 'X25519-MLKem1024',
            privateKey: newPrivateKeys[keyId],
            keyIndex: currentIndex + 1,
            status: 'active',
        });
    }
    return next;
}

/** Mark an existing key as revoked by keyId and keyIndex. */
export function revokeKeyBagEntry(bag: KeyBag, keyId: string, keyIndex: number): KeyBag {
    return {
        entries: bag.entries.map(e =>
            e.keyId === keyId && (e.keyIndex ?? 0) === keyIndex
                ? { ...e, status: 'revoked' as const }
                : e
        ),
    };
}
